import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Landmark, Loader2, CheckCircle2, AlertCircle, ShieldCheck } from "lucide-react";
import Button from "../../components/ui/Button";
import { aaLinkInfo, aaLinkStart } from "../../Utils/api";

// Opened from the link a credit manager emails (no login, the signed token in the URL is the credential).
//   active -> the customer starts the Account Aggregator consent; the link is used up and they go to Ignosis
//   used   -> Ignosis sent the customer back here (?trackingId=...&status=...), or the link was opened again:
//             shows the status of the consent the link started. A used link cannot start a second one.

const POLL_INTERVAL_MS = 5000;
const POLL_LIMIT = 24; // about 2 minutes

const BLOCKED = {
  invalid: ["This link is not valid", "Please ask your credit manager for a new link."],
  expired: ["This link has expired", "Please ask your credit manager for a new link."],
  replaced: ["A newer link has been sent to you", "Please open the latest email from SalaryMates and use that link."],
  closed: ["This application is no longer open", "The link cannot be used any more. Please contact SalaryMates if you have any questions."],
  already_done: ["Bank statement already received", "We already have your bank statement. Thank you!"],
};

const NEW_LINK = "If you still need to share your statement, please ask your credit manager for a new link.";

function StatusCard({ tone, icon, title, children }) {
  const tones = {
    green: "border-green-200 bg-green-50",
    blue: "border-blue-200 bg-blue-50",
    amber: "border-amber-200 bg-amber-50",
    red: "border-red-200 bg-red-50",
  };
  return (
    <div className={`rounded-2xl border p-5 sm:p-6 flex gap-4 items-start ${tones[tone]}`}>
      {icon}
      <div>
        <h4 className="font-semibold text-base sm:text-lg text-[#1D3557]">{title}</h4>
        <div className="text-sm sm:text-base text-gray-600 mt-1">{children}</div>
      </div>
    </div>
  );
}

function AccountAggregatorLinkPage() {
  const { token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [phase, setPhase] = useState("loading"); // loading | load-error | blocked | active | used
  const [blocked, setBlocked] = useState("invalid");
  const [info, setInfo] = useState({});
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [timedOut, setTimedOut] = useState(false);
  const pollCount = useRef(0);
  const pollTimer = useRef(null);
  const mounted = useRef(true);

  const showState = (data, keepPolling) => {
    setInfo(data);
    if (data.state === "active") {
      setPhase("active");
      return;
    }
    if (data.state !== "used") {
      setBlocked(BLOCKED[data.state] ? data.state : "invalid");
      setPhase("blocked");
      return;
    }
    setPhase("used");

    // Approved on the Ignosis side: the statement fetch takes a few seconds, keep asking.
    const waiting = data.aa_status === "PROCESSING" || (keepPolling && data.aa_status === "PENDING");
    if (waiting && pollCount.current < POLL_LIMIT) {
      pollCount.current += 1;
      pollTimer.current = setTimeout(() => load(keepPolling), POLL_INTERVAL_MS);
    } else if (waiting) {
      setTimedOut(true);
    }
  };

  const load = async (keepPolling) => {
    const resp = await aaLinkInfo(token);
    if (!mounted.current) return;
    if (!resp || resp.Status !== 1) {
      setPhase("load-error");
      return;
    }
    showState(resp.Data || {}, keepPolling);
  };

  useEffect(() => {
    mounted.current = true;
    const query = new URLSearchParams(location.search);
    const approved = query.get("status") === "APPROVED";

    if (query.get("trackingId") || query.get("status")) {
      // Drop Ignosis' query params so a refresh does not re-read them.
      navigate(`/account-aggregator/${token}`, { replace: true });
    }

    load(approved);

    return () => {
      mounted.current = false;
      clearTimeout(pollTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // the link must not be indexed or leaked through the Referer header
  useEffect(() => {
    const tags = [
      ["robots", "noindex,nofollow"],
      ["referrer", "no-referrer"],
    ].map(([name, content]) => {
      const el = document.createElement("meta");
      el.name = name;
      el.content = content;
      document.head.appendChild(el);
      return el;
    });
    return () => tags.forEach((el) => el.remove());
  }, []);

  const start = async () => {
    setStarting(true);
    setError("");
    // The login-free page is per origin too (www vs non-www): come back to this one.
    const resp = await aaLinkStart(token, window.location.origin);
    const data = resp?.Data || {};

    if (resp?.Status === 1 && data.redirection_url) {
      window.location.href = data.redirection_url;
      return;
    }

    setStarting(false);
    if (data.state === "used") {
      load(false); // started from another tab or click in the meantime: show that consent
    } else if (data.state && data.state !== "active") {
      showState(data, false);
    } else {
      setError(resp?.Message || "Unable to connect to your bank right now. Please try again.");
    }
  };

  const checkAgain = () => {
    pollCount.current = 0;
    setTimedOut(false);
    setPhase("loading");
    load(false);
  };

  const renderUsed = () => {
    const status = info.aa_status || "";

    if (status === "COMPLETED") {
      return (
        <StatusCard tone="green" icon={<CheckCircle2 className="text-green-600 shrink-0" size={28} />} title="Bank statement received">
          Thank you! We have received your bank statement and your application will move ahead. You can close this page now.
        </StatusCard>
      );
    }

    if (status === "PROCESSING") {
      return (
        <>
          <StatusCard
            tone="blue"
            icon={timedOut ? <AlertCircle className="text-[#2D5F7A] shrink-0" size={28} /> : <Loader2 className="text-[#2D5F7A] animate-spin shrink-0" size={28} />}
            title={timedOut ? "Still waiting for your bank" : "Fetching your bank statement"}
          >
            {timedOut
              ? "Your bank is taking longer than usual. We will pick the statement up automatically, so you can close this page, or check again in a while."
              : "Consent approved. Your bank is sharing the statement with us - this usually takes less than a minute. Please keep this page open."}
          </StatusCard>
          {timedOut && (
            <div className="mt-6">
              <Button className="w-full bg-[#2D5F7A]" title="Check Again" onClick={checkAgain} />
            </div>
          )}
        </>
      );
    }

    if (status === "PENDING") {
      return (
        <StatusCard tone="amber" icon={<AlertCircle className="text-amber-600 shrink-0" size={28} />} title="Consent not completed">
          This link was used to start sharing your statement, but the consent was not approved. {NEW_LINK}
        </StatusCard>
      );
    }

    if (status === "REJECTED" || status === "FAILED") {
      return (
        <StatusCard
          tone="red"
          icon={<AlertCircle className="text-red-600 shrink-0" size={28} />}
          title={status === "REJECTED" ? "Consent not approved" : "We couldn't fetch your statement"}
        >
          <p>{info.message}</p>
          <p className="text-sm text-gray-500 mt-2">
            {status === "FAILED" && info.fallback_allowed === 1
              ? "You can log in to SalaryMates and upload your bank statement PDF instead, or ask your credit manager for a new link."
              : NEW_LINK}
          </p>
        </StatusCard>
      );
    }

    return (
      <StatusCard tone="amber" icon={<AlertCircle className="text-amber-600 shrink-0" size={28} />} title="This link has already been used">
        {NEW_LINK}
      </StatusCard>
    );
  };

  return (
    <div className="w-full min-h-screen bg-[#F4F8FA] p-3 sm:p-5 md:p-8 flex items-start sm:items-center justify-center">
      <div className="w-full max-w-2xl bg-white rounded-2xl md:rounded-3xl shadow-lg p-4 sm:p-6 md:p-10">
        <div className="flex gap-4 items-start">
          <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#EAF2F6] rounded-full flex items-center justify-center shrink-0">
            <Landmark className="text-[#2D5F7A]" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1D3557]">Share Your Bank Statement</h2>
            <p className="text-gray-500 mt-2 text-sm sm:text-base">
              Securely share your last 6 months' bank statement directly from your bank through the RBI-regulated Account Aggregator. No PDF upload and no login needed.
            </p>
          </div>
        </div>

        <div className="mt-8">
          {phase === "loading" && (
            <div className="flex items-center justify-center gap-3 py-12 text-gray-500">
              <Loader2 className="animate-spin" size={22} />
              <span>Checking your link...</span>
            </div>
          )}

          {phase === "load-error" && (
            <>
              <StatusCard tone="red" icon={<AlertCircle className="text-red-600 shrink-0" size={28} />} title="Something went wrong">
                We could not check your link. Please check your internet connection and try again.
              </StatusCard>
              <div className="mt-6">
                <Button className="w-full bg-[#2D5F7A]" title="Try Again" onClick={checkAgain} />
              </div>
            </>
          )}

          {phase === "blocked" && (
            <StatusCard
              tone={blocked === "already_done" ? "green" : "amber"}
              icon={blocked === "already_done" ? <CheckCircle2 className="text-green-600 shrink-0" size={28} /> : <AlertCircle className="text-amber-600 shrink-0" size={28} />}
              title={BLOCKED[blocked][0]}
            >
              {BLOCKED[blocked][1]}
            </StatusCard>
          )}

          {phase === "active" && (
            <>
              {info.name && <p className="text-base sm:text-lg text-[#1D3557] mb-4">Hello {info.name},</p>}
              <ol className="space-y-3 text-sm sm:text-base text-gray-600">
                {[
                  "Verify with an OTP sent to your bank-registered mobile number.",
                  "Select the bank account your salary is credited to.",
                  "Approve the consent. Your statement is shared read-only.",
                ].map((step, index) => (
                  <li key={step} className="flex gap-3 items-start">
                    <span className="w-6 h-6 rounded-full bg-[#EAF2F6] text-[#2D5F7A] text-xs font-semibold flex items-center justify-center shrink-0 mt-0.5">{index + 1}</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>

              {error && (
                <div role="alert" className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="mt-8">
                <Button className="w-full bg-[#2D5F7A]" title="Connect Bank Account" onClick={start} loading={starting} />
              </div>
              <p className="mt-3 text-xs sm:text-sm text-gray-500 text-center">
                This link works once{info.expires_on ? `, until ${info.expires_on} (IST)` : ""}. Please do not share it.
              </p>
            </>
          )}

          {phase === "used" && renderUsed()}

          <div className="mt-8 flex gap-3 items-start text-xs sm:text-sm text-gray-500">
            <ShieldCheck className="text-green-600 shrink-0" size={18} />
            <span>Account Aggregator is an RBI-regulated framework. Your bank shares a read-only statement with your consent; we never see your net-banking login.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountAggregatorLinkPage;
